import { Component, OnInit, Injectable, Inject, HostListener, ViewEncapsulation } from '@angular/core';
import { trigger, state, style, animate, transition } from '@angular/animations';
import { Router, ActivatedRoute, NavigationStart, Event as NavigationEvent } from '@angular/router';
import { unescape } from 'lodash';
import { Observable } from 'rxjs';

import { map, filter } from 'rxjs/operators';

import { PollDataService, TrackingService } from '@services/index';
import { AuthService } from '@examsoft/es-login';

import Poll from '@shared/types/poll.type';
import PollStatus from '@shared/types/poll-status.type';
import Response from '@shared/types/response.type';
import Question from '@shared/types/question.type';

@Component({
  selector: 'poll-question',
  templateUrl: './poll-question.component.html',
  styleUrls: ['./poll-question.component.scss'],
  encapsulation: ViewEncapsulation.ShadowDom,
  animations: [
    trigger('questionState', [
      state(
        'notReady',
        style({
          opacity: 0,
          transform: 'translateX(100px)',
        })
      ),
      transition('notReady => ready', [
        style({
          opacity: 0,
        }),
        animate(
          '100ms ease-in',
          style({
            opacity: 1,
            transform: 'translateX(0)',
          })
        ),
      ]),
    ]),
  ],
})
export class PollQuestionComponent implements OnInit {
  pollId;
  poll: Poll;
  responses: any[];
  question: Question;
  questionState: string = 'notReady';
  questionIndex: number = 0;
  selectedChoices: number[] = [];
  shortAnswers: any[] = [];
  pollStatus: PollStatus;
  statusPrompt: 'WaitingToBegin' | 'WaitingForNextQuestion' | 'Completed' | 'Ready' | 'WaitingForCompleted';
  questionAnswered: boolean = false;
  esId: string;
  pageChange;

  private pollSubscription: any;
  private responseDeleteSubscription: any;
  private questionStatusSubscription: any;

  constructor(
    private trackingService: TrackingService,
    private authService: AuthService,
    private pollDataService: PollDataService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.esId = this.authService.user['custom:es-id'];
    router.events
      .pipe(
        filter((event: NavigationEvent) => {
          return event instanceof NavigationStart;
        })
      )
      .subscribe((event: NavigationStart) => {
        this.pageChange = event.navigationTrigger;
      });
  }

  async ngOnInit() {
    await this.onPollUpdatedSubscription();
    this.onQuestionStatusUpdatedSubscription();
    this.onDeletedResponseSubscription();
  }

  private async onPollUpdatedSubscription() {
    this.pollId = this.route.snapshot.params.pollid;
    this.poll = await this.pollDataService.getPoll(this.pollId);
    this.question = await this.pollDataService.getPollQuestion(this.pollId, this.poll.currentQuestion);
    this.responses = await this.pollDataService.getStudentResponses(
      this.esId,
      `response#${this.pollId}#${this.question.id}`
    );
    this.pollDataService.updateLastAccessed(this.esId, this.pollId);

    this.setPollDetails();
    this.setQuestionStatus();
    this.setStatusPrompt();

    if (this.pollSubscription && this.pollSubscription.unsubscribe) {
      this.pollSubscription.unsubscribe();
    }
    this.pollSubscription = await this.pollDataService.getPollUpdates(this.pollId).subscribe({
      next: async (poll) => this.handlePollUpdated(poll),
      error: (err) => {
        this.pollSubscription.unsubscribe();
        this.onPollUpdatedSubscription();
      },
    });
    return;
  }

  private async handlePollUpdated(poll) {
    this.poll = {
      ...this.poll,
      ...poll,
    };
    if (this.poll.status === 'complete') {
      this.routeCompletedPoll();
      return;
    }
    this.pollId = this.poll.id;
    this.questionState = 'notReady';
    this.question = await this.pollDataService.getPollQuestion(this.pollId, this.poll.currentQuestion);
    this.responses = await this.pollDataService.getStudentResponses(
      this.esId,
      `response#${this.pollId}#${this.question.id}`
    );
    this.setPollDetails();
    this.setQuestionStatus();
    this.setStatusPrompt();

    this.onQuestionStatusUpdatedSubscription();
    this.onDeletedResponseSubscription();
  }

  private async onQuestionStatusUpdatedSubscription() {
    if (this.questionStatusSubscription && this.questionStatusSubscription.unsubscribe) {
      this.questionStatusSubscription.unsubscribe();
    }
    this.questionStatusSubscription = this.pollDataService
      .getQuestionStatusUpdates(this.pollId, this.question.id)
      .subscribe({
        next: (q) => this.handleQuestionStatusUpdated(q),
        error: (err) => {
          this.questionStatusSubscription.unsubscribe();
          this.onQuestionStatusUpdatedSubscription();
        },
      });
  }

  private handleQuestionStatusUpdated(question) {
    this.question = {
      ...this.question,
      questionStatus: question.questionStatus,
    };
    this.setPollDetails();
    this.setQuestionStatus();
    this.setStatusPrompt();
  }

  private async onDeletedResponseSubscription() {
    if (this.responseDeleteSubscription && this.responseDeleteSubscription.unsubscribe) {
      this.responseDeleteSubscription.unsubscribe();
    }

    this.responseDeleteSubscription = this.pollDataService
      .getResponseDeleteUpdates(this.esId, `response#${this.poll.id}#${this.question.id}`)
      .subscribe({
        next: (r) => this.handleDeletedResponse(r),
        error: (err) => {
          this.responseDeleteSubscription.unsubscribe();
          this.onDeletedResponseSubscription();
        },
      });
  }

  private handleDeletedResponse(response) {
    this.responses = this.responses.filter((resp) => {
      return resp.userId !== response.userId && resp.responseId !== response.responseId;
    });
    this.setPollDetails();
    this.setQuestionStatus();
    this.setStatusPrompt();
  }

  private setQuestionStatus() {
    if (!this.responses || this.responses.length === 0) {
      this.questionAnswered = false;
      return;
    }
    this.questionAnswered =
      this.responses.findIndex((resp) => {
        return `response#${this.poll.id}#${this.question.id}` === resp.responseId;
      }) > -1;
  }

  private setPollDetails() {
    this.pollStatus = this.poll.status;
    this.questionIndex = this.poll.currentQuestion;
    if (this.question.type === 'short_answer') {
      this.shortAnswers = this.question.fitbAnswer.blankAnswers.map((choice, idx) => {
        console.log('What is Choice in Poll Question', choice);
        return {
          blankUID: choice.blankUID,
          userAnswer: '',
          choiceType: choice.choiceType,
          options: this.convertOptions(choice.options),
        };
      });
    }
    this.questionState = 'ready';
  }

  private convertOptions(options) {
    let convertedOptions = [];
    options.forEach((option) => {
      let convertedOption = unescape(option);
      convertedOptions.push(convertedOption);
    });
    return convertedOptions;
  }

  ngOnDestroy() {
    this.pollSubscription && this.pollSubscription.unsubscribe();
    this.responseDeleteSubscription && this.responseDeleteSubscription.unsubscribe();
    this.questionStatusSubscription && this.questionStatusSubscription.unsubscribe();
  }

  private setStatusPrompt() {
    if (this.pollStatus === 'notactive') {
      this.statusPrompt = 'WaitingToBegin';
    } else if (
      (this.question.questionStatus === 'closed' && !this.isLastQuestion()) ||
      (this.pollStatus === 'active' && this.questionAnswered && !this.isLastQuestion())
    ) {
      this.statusPrompt = 'WaitingForNextQuestion';
    } else if (
      ((this.pollStatus === 'active' && this.questionAnswered) || this.question.questionStatus === 'closed') &&
      this.isLastQuestion()
    ) {
      this.statusPrompt = 'WaitingForCompleted';
    } else if (this.pollStatus === 'active' && !this.questionAnswered) {
      this.statusPrompt = 'Ready';
    }

    this.trackingService.addPageAction('student-poll-status', {
      pollId: this.poll.id,
      questionId: this.question.id,
      status: this.statusPrompt,
    });
    this.questionState = 'ready';
  }

  private routeCompletedPoll() {
    this.pollDataService.completeUserPoll(this.esId, this.pollId).then(() => {
      this.statusPrompt = 'Completed';
      this.pollDataService.pollHistory.next(this.poll.id);
      this.router.navigate(['launcher']);
    });
  }

  onSubmit() {
    const pollQuestionId = this.poll.id + ':' + this.question.id;
    const questionResponse: Promise<Response> =
      this.question.type === 'short_answer' ? this.addShortAnswerResponse() : this.addMCAnswerResponse();

    this.questionState = 'notReady';

    this.trackingService.addPageAction('student-poll-submit', {
      pollId: this.poll.id,
      questionId: this.question.id,
      status: this.statusPrompt,
    });

    questionResponse.then((data) => {
      this.responses.push(data);
      this.selectedChoices = [];
      this.setPollDetails();
      this.setQuestionStatus();
      this.setStatusPrompt();
    });
  }

  private addShortAnswerResponse() {
    const responseDetails = {
      userId: this.esId,
      portalQid: this.question.id,
      responseType: 'text',
      shortAnswerBlanks: this.shortAnswers.filter(
        (data) => data.userAnswer.length || typeof data.userAnswer === 'number'
      ),
    };
    //We need to return an array because mc questions support multiple responses
    return this.pollDataService.addResponseToPoll(this.esId, this.question.id, this.pollId, responseDetails);
  }

  private addMCAnswerResponse() {
    let responseDetails = {
      userId: this.esId,
      portalQid: this.question.id,
      responseType: 'index',
      choiceIndexes: [],
    };

    this.selectedChoices.forEach((choice) => {
      responseDetails.choiceIndexes.push(choice);
    });
    return this.pollDataService.addResponseToPoll(this.esId, this.question.id, this.pollId, responseDetails);
  }

  receiveChoice($event) {
    this.selectedChoices = $event;
  }

  receiveAnswer($event, i) {
    this.shortAnswers[i].userAnswer = $event;
  }

  private isLastQuestion() {
    return !(this.poll && this.questionIndex < this.poll.numQuestions - 1);
  }

  canSubmitAnswer(): boolean {
    const checkMcAnswer = () => this.selectedChoices.length;
    const checkShortAnswer = () => {
      let answerCounter = 0;
      this.shortAnswers.forEach((answer) => {
        if (answer.userAnswer !== '' && answer.userAnswer !== undefined) {
          answerCounter++;
        }
      });
      if (answerCounter === this.shortAnswers.length) {
        return true;
      } else {
        return false;
      }
    };

    const qAnsweredChecks = {
      mc: checkMcAnswer,
      mc_multi: checkMcAnswer,
      tf: checkMcAnswer,
      short_answer: checkShortAnswer,
    };

    const validator = qAnsweredChecks[this.question.type] || checkMcAnswer;
    return validator();
  }

  @HostListener('window:online')
  onOnline() {
    window.location.reload();
  }
}
